import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import { asUnified, ut } from '@devnw/theme'
// eslint-import-resolver-node (the resolver wired in .eslintrc.json) does not
// understand package.json "exports" maps, so it cannot follow @devnw's
// wildcard subpath export. The module resolves correctly at build/runtime
// (webpack, Node, ts-jest) via the "exports" field — see
// __tests__/devnw-smoke.test.tsx for the Phase 0 proof.
// eslint-disable-next-line import/no-unresolved
import EvidenceCaseFile from '@devnw/cyber-iam/iam/EvidenceCaseFile/EvidenceCaseFile'
import { useAccessRequestsApi } from '../api/accessRequestsClient'
import type { EvidenceRecord as WireEvidenceRecord } from '../api/types'
import { toIamEvidenceRecord } from '../api/evidenceMapping'
import { useActiveTenant } from '../layout/TenantSwitcher'
import { PamLoading, PamError, PamEmpty } from '../components/PamStateComponents'
import { PamBreadcrumbs } from '../components/PamBreadcrumbs'

function errMessage(e: unknown): string {
  return e instanceof Error ? e.message : 'Load failed'
}

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => ut(asUnified(theme)).space('md')};
`

/**
 * OIDCPAM Gate-6 Phase 6d: migrated off MUI onto
 * @devnw/cyber-iam/iam/EvidenceCaseFile — the request record, every
 * approval decision, and the activated grant (when one exists) are all
 * rendered by the library. This page still owns the evidence fetch (via
 * useAccessRequestsApi — see that hook's deepSnake() doc comment),
 * tenant/routing, and the wire-mapping to the library's camelCase
 * EvidenceRecord shape (see evidenceMapping.ts).
 */
export const EvidenceView = (): JSX.Element => {
  const { requestId } = useParams<{ requestId: string }>()
  const { tenant } = useActiveTenant()
  const accessRequestsApi = useAccessRequestsApi()
  const [evidence, setEvidence] = useState<WireEvidenceRecord | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!requestId) return
    setLoading(true)
    accessRequestsApi
      .evidence(tenant, requestId)
      .then(setEvidence)
      .catch((e: unknown) => setError(errMessage(e)))
      .finally(() => setLoading(false))
  }, [tenant, requestId])

  if (loading) return <PamLoading />
  if (error) {
    return <PamError message={error} />
  }
  if (!evidence) return <PamEmpty message="Evidence not found" />

  const idLabel = requestId ? `${requestId.slice(0, 8)}...` : '...'

  return (
    <PageContainer>
      <PamBreadcrumbs
        crumbs={[
          { label: 'Access Requests', to: '/admin/access-requests' },
          {
            label: idLabel,
            to: `/admin/access-requests/${requestId ?? ''}`,
          },
          { label: 'Evidence' },
        ]}
      />
      <EvidenceCaseFile
        testID="evidence-case-file"
        title="Evidence Case File"
        evidence={toIamEvidenceRecord(evidence)}
      />
    </PageContainer>
  )
}
